/**
 * The shared whiteboard in the studio: a 6 x 2 m canvas panel everyone draws on with the
 * pointer ray (trigger or mouse button held) or a tracked finger, a tool tray on its left with
 * pens in three widths and six colours, an eraser, sticky notes, undo and a snapshot button.
 * Strokes stream to the room while the pen is down; the server keeps the board for late
 * joiners. Snapshots are saved as PNG room assets and listed on the /queries page.
 */
import * as THREE from 'three';
import { WHITEBOARD, type WbNote, type WbStroke } from '@clinspace/shared';
import { CanvasPanel, type Region } from '../dashboards/CanvasPanel.ts';
import {
  PALETTE,
  chip,
  fillRoundRect,
  font,
  text,
  wrapText,
  panelBackground,
} from '../dashboards/charts.ts';
import { api, sendJson } from '../lib/api.ts';
import type { FloatingPanelSounds } from '../queries/FloatingPanel.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import { STUDIO, STUDIO_FITTINGS } from '../world/plan.ts';
import type { InputManager, Pointer } from '../xr/InputManager.ts';
import type { Net } from '../app/net.ts';
import { NoteEditor, type NoteDraft } from './NoteEditor.ts';
import {
  appendPoint,
  drawBoard,
  eraseAt,
  noteAt,
  noteRect,
  widthLabel,
  type WbTool,
} from './board.ts';

/**
 * Where the board hangs: the collaboration studio's back wall (2026-10-07; it was on the hall's
 * west wall), 0.6 to 2.6 m up, facing into the studio.
 */
export const BOARD_POSE = {
  x: STUDIO.x0 + 0.07,
  y: 1.6,
  z: STUDIO_FITTINGS.boardZ,
  yaw: Math.PI / 2,
};
/** Beside the board at its entrance end (+z), within reach of someone drawing. */
const TRAY_POSE = { x: STUDIO.x0 + 0.65, y: 1.05, z: STUDIO_FITTINGS.trayZ };
const BOARD_PX = { w: 1536, h: 512 };
const TRAY_PX = { w: 520, h: 400 };
/** In-progress strokes go out at this rate. */
const STREAM_MS = 100;
/** The ray must be reasonably close; nobody draws from across the hall. */
const MAX_REACH = 5;
const TOUCH_DEPTH = 0.025;

interface TrayRegion {
  tool?: WbTool;
  colour?: string;
  width?: number;
  noteColour?: string;
  action?: 'undo' | 'clear' | 'snapshot';
}

export interface WhiteboardHandlers {
  tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
  notice(lines: string[], ms?: number): void;
  /** The viewer's head, for placing the note editor. */
  head(): THREE.Vector3;
  name(): string;
  study(): string;
}

export class Whiteboard {
  readonly group = new THREE.Group();
  readonly board: CanvasPanel;
  readonly tray: CanvasPanel;
  readonly editor: NoteEditor;
  tool: WbTool = 'pen';
  colour: string = WHITEBOARD.colours[1];
  width: number = WHITEBOARD.widths[1];
  noteColour: string = WHITEBOARD.noteColours[0];
  /** The stroke the local user is drawing. */
  private live: WbStroke | undefined;
  private livePointer: string | undefined;
  private lastStream = 0;
  private hoveredNote: string | undefined;
  private strokeCounter = 0;
  private readonly raycaster = new THREE.Raycaster();
  private readonly tmp = new THREE.Vector3();
  private readonly unsubscribe: (() => void)[] = [];
  /** Dev hooks: the last snapshot saved, the last erase sent. */
  lastSnapshot: { file: string; study: string } | undefined;
  lastErase: string[] = [];

  constructor(
    private readonly net: Net,
    private readonly interactive: InteractiveSystem,
    private readonly input: InputManager,
    private readonly handlers: WhiteboardHandlers,
    sounds: FloatingPanelSounds,
  ) {
    this.group.name = 'whiteboard';
    this.board = new CanvasPanel({
      width: WHITEBOARD.width,
      height: WHITEBOARD.height,
      pixelWidth: BOARD_PX.w,
      pixelHeight: BOARD_PX.h,
      name: 'whiteboard-board',
      resolution: 1,
    });
    this.board.mesh.position.set(BOARD_POSE.x, BOARD_POSE.y, BOARD_POSE.z);
    this.board.mesh.rotation.y = BOARD_POSE.yaw;
    this.board.setDraw((ctx, panel) => this.drawBoard(ctx, panel));
    this.board.onSelect = (region, pointer, point) => this.selectBoard(region, pointer, point);
    this.board.onHoverChange = (region, point) => {
      const note = region?.id.startsWith('note:') ? (region.data as WbNote) : undefined;
      this.hoveredNote = note?.id;
      handlers.tooltip(region?.tooltip, region && note ? point : undefined);
    };
    this.board.attach(interactive);
    this.group.add(this.board.mesh);

    // The tool tray: a tilted plate on a stand at the entrance end of the board.
    this.tray = new CanvasPanel({
      width: 0.65,
      height: 0.5,
      pixelWidth: TRAY_PX.w,
      pixelHeight: TRAY_PX.h,
      name: 'whiteboard-tray',
      resolution: 1.5,
    });
    const trayGroup = new THREE.Group();
    trayGroup.position.set(TRAY_POSE.x, TRAY_POSE.y, TRAY_POSE.z);
    trayGroup.rotation.y = Math.PI / 2;
    this.tray.mesh.rotation.x = -0.55;
    trayGroup.add(this.tray.mesh);
    const stand = new THREE.Mesh(
      new THREE.CylinderGeometry(0.03, 0.05, TRAY_POSE.y - 0.1, 12),
      new THREE.MeshStandardMaterial({ color: '#a8a39c', roughness: 0.3, metalness: 0.85 }),
    );
    stand.position.set(TRAY_POSE.x, (TRAY_POSE.y - 0.1) / 2, TRAY_POSE.z);
    this.group.add(trayGroup, stand);
    this.tray.setDraw((ctx, panel) => this.drawTray(ctx, panel));
    this.tray.onSelect = (region) => this.selectTray(region);
    this.tray.onHoverChange = (region, point) =>
      handlers.tooltip(region?.tooltip, region ? point : undefined);
    this.tray.attach(interactive);

    this.editor = new NoteEditor(
      interactive,
      { save: (draft, body) => this.saveNote(draft, body) },
      sounds,
    );
    this.group.add(this.editor.group);

    this.unsubscribe.push(
      net.onWhiteboard(() => this.invalidate()),
      net.onStroke(() => this.board.invalidate()),
      net.onNote(() => this.invalidate()),
      net.onErase(() => this.invalidate()),
    );
  }

  get typing(): boolean {
    return this.editor.typing;
  }

  get strokeCount(): number {
    return this.net.whiteboard.strokes.length;
  }

  get noteCount(): number {
    return this.net.whiteboard.notes.length;
  }

  /** The local stroke in progress, if any (dev hook). */
  get drawing(): boolean {
    return !!this.live;
  }

  private invalidate(): void {
    this.board.invalidate();
    this.tray.invalidate();
  }

  setTool(tool: WbTool): void {
    this.tool = tool;
    this.tray.invalidate();
  }

  handleKeyEvent(e: KeyboardEvent): boolean {
    return this.editor.handleKeyEvent(e);
  }

  /* ---------- Per frame: pointer sampling ---------- */

  update(now = performance.now()): void {
    for (const p of this.input.activePointers()) this.sample(p, now);
    // A pointer that vanished (controller off) ends the stroke.
    if (this.live && !this.input.activePointers().some((p) => p.id === this.livePointer))
      this.finishStroke();
    this.board.update(now);
    this.tray.update(now);
    this.editor.update();
  }

  /** Board coordinates under a pointer: the ray hit, or a tracked finger touching the board. */
  private boardPoint(p: Pointer): { u: number; v: number; pressed: boolean } | undefined {
    if (p.kind === 'hand' && p.indexTip) {
      const local = this.board.mesh.worldToLocal(this.tmp.copy(p.indexTip));
      if (
        Math.abs(local.z) < TOUCH_DEPTH &&
        Math.abs(local.x) <= WHITEBOARD.width / 2 &&
        Math.abs(local.y) <= WHITEBOARD.height / 2
      )
        return {
          u: local.x / WHITEBOARD.width + 0.5,
          v: 0.5 - local.y / WHITEBOARD.height,
          pressed: true,
        };
    }
    this.raycaster.set(p.ray.origin, p.ray.direction);
    this.raycaster.far = MAX_REACH;
    const hit = this.raycaster.intersectObject(this.board.mesh, false)[0];
    if (!hit) return undefined;
    const px = this.board.toPixel(hit.point);
    return { u: px.x / BOARD_PX.w, v: px.y / BOARD_PX.h, pressed: p.selecting };
  }

  private sample(p: Pointer, now: number): void {
    if (this.editor.isOpen) return;
    const at = this.boardPoint(p);
    const pressed = !!at?.pressed;
    if (this.live && this.livePointer === p.id) {
      if (pressed && at) {
        if (appendPoint(this.live.points, at.u, at.v)) this.board.invalidate();
        if (now - this.lastStream >= STREAM_MS) {
          this.lastStream = now;
          this.net.wbStroke({ ...this.live, done: false });
        }
      } else this.finishStroke();
      return;
    }
    if (!pressed || !at) return;
    if (this.tool === 'pen') {
      if (this.live) return; // another pointer is drawing
      this.strokeCounter++;
      this.live = {
        id: `${(this.net.sessionId ?? 'local').slice(-8)}-${this.strokeCounter}-${now.toString(36)}`.slice(
          0,
          40,
        ),
        by: { sessionId: this.net.sessionId ?? 'local', name: this.handlers.name() },
        colour: this.colour,
        width: this.width,
        points: [at.u, at.v],
        done: false,
      };
      this.livePointer = p.id;
      this.lastStream = now;
      this.board.invalidate();
    } else if (this.tool === 'eraser') {
      this.erase(at.u, at.v);
    }
  }

  private finishStroke(): void {
    const stroke = this.live;
    this.live = undefined;
    this.livePointer = undefined;
    if (!stroke) return;
    stroke.done = true;
    // The server never echoes a stroke to its author, so keep our own copy.
    const list = this.net.whiteboard.strokes;
    const i = list.findIndex((s) => s.id === stroke.id);
    if (i >= 0) list[i] = stroke;
    else list.push(stroke);
    this.net.wbStroke(stroke);
    this.invalidate();
  }

  erase(u: number, v: number): string[] {
    const ids = eraseAt(this.net.whiteboard, u, v);
    if (!ids.length) return ids;
    const gone = new Set(ids);
    this.net.whiteboard.strokes = this.net.whiteboard.strokes.filter((s) => !gone.has(s.id));
    this.net.whiteboard.notes = this.net.whiteboard.notes.filter((n) => !gone.has(n.id));
    this.net.wbErase(ids);
    this.lastErase = ids;
    this.invalidate();
    return ids;
  }

  /* ---------- Board ---------- */

  private drawBoard(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const state = this.net.whiteboard;
    drawBoard(ctx, state, panel.pxW, panel.pxH, {
      hoveredNote: this.hoveredNote,
      live: this.live,
      wrap: wrapText,
      font,
    });
    panel.region({ id: 'board', x: 0, y: 0, w: panel.pxW, h: panel.pxH, selectable: false });
    for (const n of state.notes) {
      const r = noteRect(n);
      panel.region<WbNote>({
        id: `note:${n.id}`,
        x: r.u * panel.pxW,
        y: r.v * panel.pxH,
        w: r.w * panel.pxW,
        h: r.h * panel.pxH,
        data: n,
        tooltip: [
          n.text,
          `${n.by.name}`,
          this.tool === 'eraser' ? 'Select to remove' : 'Select to edit (Note tool)',
        ],
      });
    }
    if (state.strokes.length === 0 && state.notes.length === 0 && !this.live) {
      text(ctx, 'Shared whiteboard: hold the trigger (or the mouse button) and draw.', 30, 60, {
        size: 16,
        colour: PALETTE.muted,
      });
    }
  }

  private selectBoard(region: Region, _pointer: Pointer, point: THREE.Vector3): void {
    const px = this.board.toPixel(point);
    const u = px.x / BOARD_PX.w;
    const v = px.y / BOARD_PX.h;
    const note = region.id.startsWith('note:') ? (region.data as WbNote) : undefined;
    if (this.tool === 'eraser') {
      if (note) this.erase(u, v);
      return;
    }
    if (this.tool !== 'note') return;
    const existing = note ?? noteAt(this.net.whiteboard.notes, u, v);
    const draft: NoteDraft = existing
      ? { id: existing.id, u: existing.u, v: existing.v, colour: existing.colour }
      : { u: Math.max(0, u - 0.02), v: Math.max(0, v - 0.05), colour: this.noteColour };
    this.editor.open(draft, existing?.text ?? '', point, this.handlers.head());
  }

  private saveNote(draft: NoteDraft, body: string): void {
    this.net.wbNote({
      ...(draft.id ? { id: draft.id } : {}),
      u: draft.u,
      v: draft.v,
      text: body,
      colour: draft.colour,
    });
    this.noteColour = draft.colour;
  }

  /** Dev hook: a note without the editor. */
  addNote(u: number, v: number, body: string): void {
    this.net.wbNote({ u, v, text: body, colour: this.noteColour });
  }

  /** Dev hook: a finished stroke straight from a list of board points. */
  drawStroke(points: [number, number][]): string {
    this.strokeCounter++;
    const stroke: WbStroke = {
      id: `${(this.net.sessionId ?? 'local').slice(-8)}-${this.strokeCounter}-${Date.now().toString(36)}`.slice(
        0,
        40,
      ),
      by: { sessionId: this.net.sessionId ?? 'local', name: this.handlers.name() },
      colour: this.colour,
      width: this.width,
      points: points.flat(),
      done: true,
    };
    this.net.whiteboard.strokes.push(stroke);
    this.net.wbStroke(stroke);
    this.invalidate();
    return stroke.id;
  }

  /* ---------- Tray ---------- */

  private drawTray(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    panelBackground(ctx, W, H, 16);
    text(ctx, 'WHITEBOARD', 20, 32, { size: 15, weight: 700, colour: PALETTE.teal });
    const state = this.net.whiteboard;
    text(
      ctx,
      `${state.strokes.length} stroke${state.strokes.length === 1 ? '' : 's'} · ${state.notes.length} note${state.notes.length === 1 ? '' : 's'}`,
      W - 20,
      32,
      { size: 12, colour: PALETTE.muted, align: 'right' },
    );
    // Tools.
    let x = 20;
    const tools: { tool: WbTool; label: string; tip: string }[] = [
      { tool: 'pen', label: 'Pen', tip: 'Hold the trigger on the board to draw' },
      { tool: 'eraser', label: 'Eraser', tip: 'Hold the trigger over strokes or notes to erase' },
      { tool: 'note', label: 'Sticky note', tip: 'Select a spot on the board to add a note' },
    ];
    for (const t of tools) {
      const id = `tool:${t.tool}`;
      const r = chip(ctx, t.label, x, 52, {
        active: this.tool === t.tool,
        hovered: panel.isHovered(id),
        h: 34,
        size: 13,
      });
      panel.region<TrayRegion>({ id, ...r, data: { tool: t.tool }, tooltip: [t.label, t.tip] });
      x += r.w + 8;
    }
    // Colours.
    text(ctx, 'Colour', 20, 118, { size: 11, colour: PALETTE.muted });
    x = 20;
    for (const colour of WHITEBOARD.colours) {
      const r = { x, y: 128, w: 44, h: 36 };
      fillRoundRect(ctx, r, 8, colour);
      if (this.colour === colour) {
        ctx.strokeStyle = PALETTE.text;
        ctx.lineWidth = 3;
        ctx.strokeRect(r.x - 2, r.y - 2, r.w + 4, r.h + 4);
      }
      const id = `colour:${colour}`;
      panel.region<TrayRegion>({ id, ...r, data: { colour }, tooltip: ['Pen colour'] });
      x += 52;
    }
    // Widths.
    text(ctx, 'Width', 20, 192, { size: 11, colour: PALETTE.muted });
    x = 20;
    for (const width of WHITEBOARD.widths) {
      const r = { x, y: 200, w: 60, h: 40 };
      fillRoundRect(ctx, r, 8, this.width === width ? PALETTE.selected : PALETTE.raised);
      ctx.fillStyle = PALETTE.text;
      ctx.beginPath();
      ctx.arc(r.x + r.w / 2, r.y + r.h / 2, 3 + width * 1600, 0, Math.PI * 2);
      ctx.fill();
      const id = `width:${width}`;
      panel.region<TrayRegion>({
        id,
        ...r,
        data: { width },
        tooltip: [`${widthLabel(width)} pen`],
      });
      x += 68;
    }
    // Note colours.
    text(ctx, 'Note', 240, 192, { size: 11, colour: PALETTE.muted });
    x = 240;
    for (const colour of WHITEBOARD.noteColours) {
      const r = { x, y: 200, w: 40, h: 40 };
      fillRoundRect(ctx, r, 8, colour);
      if (this.noteColour === colour) {
        ctx.strokeStyle = PALETTE.text;
        ctx.lineWidth = 3;
        ctx.strokeRect(r.x - 2, r.y - 2, r.w + 4, r.h + 4);
      }
      const id = `note:${colour}`;
      panel.region<TrayRegion>({
        id,
        ...r,
        data: { noteColour: colour },
        tooltip: ['Note colour'],
      });
      x += 48;
    }
    // Actions.
    const actions: { action: TrayRegion['action']; label: string; tip: string }[] = [
      { action: 'undo', label: 'Undo mine', tip: 'Removes your last stroke or note' },
      { action: 'clear', label: 'Clear board', tip: 'Wipes the board for everyone' },
      { action: 'snapshot', label: 'Snapshot to PNG', tip: 'Saves the board to the room assets' },
    ];
    x = 20;
    for (const a of actions) {
      const id = `action:${a.action}`;
      const r = chip(ctx, a.label, x, 268, {
        hovered: panel.isHovered(id),
        h: 34,
        size: 12,
        colour: a.action === 'clear' ? PALETTE.crimson : undefined,
      });
      panel.region<TrayRegion>({ id, ...r, data: { action: a.action }, tooltip: [a.label, a.tip] });
      x += r.w + 8;
    }
    text(
      ctx,
      this.lastSnapshot
        ? `Saved ${this.lastSnapshot.file}. Snapshots are listed on the /queries page.`
        : 'Snapshots are listed on the /queries page for export.',
      20,
      336,
      { size: 11, colour: PALETTE.muted, maxWidth: W - 40 },
    );
    text(
      ctx,
      'Finger-touch works with hand tracking; G and the mouse wheel do not apply here.',
      20,
      362,
      {
        size: 10,
        colour: PALETTE.muted,
        maxWidth: W - 40,
      },
    );
  }

  private selectTray(region: Region): void {
    const data = region.data as TrayRegion | undefined;
    if (!data) return;
    if (data.tool) this.tool = data.tool;
    if (data.colour) this.colour = data.colour;
    if (data.width) this.width = data.width;
    if (data.noteColour) this.noteColour = data.noteColour;
    if (data.action === 'undo') this.undo();
    if (data.action === 'clear') this.clear();
    if (data.action === 'snapshot') void this.snapshot();
    this.tray.invalidate();
  }

  undo(): void {
    this.net.wbUndo();
  }

  clear(): void {
    this.net.wbClear();
  }

  /** Saves the board as a PNG room asset; resolves with the saved file. */
  async snapshot(): Promise<{ file: string; study: string } | undefined> {
    // Draw once more without hover highlights so the picture is clean.
    this.hoveredNote = undefined;
    this.board.invalidate();
    this.board.update();
    const image = this.board.canvas.toDataURL('image/png');
    try {
      const asset = await sendJson<{ file: string; study: string }>(
        'POST',
        api('rooms', this.handlers.study(), 'snapshots'),
        { kind: 'whiteboard', image, by: this.handlers.name() },
      );
      this.lastSnapshot = asset;
      this.handlers.notice(
        ['Whiteboard snapshot saved', asset.file, 'On the query log page, Room snapshots tab'],
        4000,
      );
      this.tray.invalidate();
      return asset;
    } catch (err) {
      this.handlers.notice(['Snapshot failed', (err as Error).message], 4000);
      return undefined;
    }
  }

  dispose(): void {
    for (const u of this.unsubscribe) u();
    this.board.detach(this.interactive);
    this.tray.detach(this.interactive);
    this.board.dispose();
    this.tray.dispose();
    this.editor.dispose();
  }
}
